import { logCustomEvent } from '@braze/web-sdk'
import { useEffect, useRef } from 'react'

import { useBraze } from '@/context/brazeContext'

import { getStoredUtms } from '@/lib/attribution/landingUtms'
import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'

const PAGE_VIEWED_EVENT = 'website-page-viewed'

/**
 * Fires a Braze `website-page-viewed` custom event on every page view
 * (initial load and client-side navigations) once the SDK is initialised.
 *
 * Event properties:
 *   - page_path        — the resolved URL path (e.g. /learn/gut-health-tips)
 *   - page_view_index  — 0-based index of this page-view within the SDK session.
 *                        Resets only on full page reload (new Braze session).
 *   - utm_* / click-ids — captured landing UTMs, so campaigns can segment on the
 *                        trigger event's properties instead of the profile's custom
 *                        attributes, which may not have propagated yet when this
 *                        event fires (avoids a Braze race condition).
 */
export const useBrazePageTracking = () => {
  const router = useTrackedRouter()
  const { initialized } = useBraze()
  const pageViewIndexRef = useRef(0)

  useEffect(() => {
    if (!initialized || !router.isReady) return

    const resolvedPath = router.asPath.split('?')[0].split('#')[0]

    logCustomEvent(PAGE_VIEWED_EVENT, {
      page_path: resolvedPath,
      page_view_index: pageViewIndexRef.current,
      ...getStoredUtms(),
    })

    pageViewIndexRef.current += 1
  }, [initialized, router.isReady, router.asPath])
}
